import React, {useState} from 'react'
import BreadcrumbsBar from "../../layouts/breadcrumb/BreadcrumbsBar";
import styles from "./ProductPerformance.module.scss";
//xml file reader
import {t} from "i18next";
import useToast from "../../common/hooks/useToast";
import Toast from "../../common/toast/Toast";
import Card from "../../common/card/Card";
import {getConversionReport} from "../../services/productPerformanceService";
import {DOWNLOAD_OFFER_PRODUCTS_EXCEL_DATA} from "../../common/constants/routes";
import ExportDropdown from "../../common/exportDropdown/ExportDropdown";
import {EXPORT_FILE_TYPES} from "../AdvertiserManagement/CreateEditPaidProject/CreateEditPaidProject";
import ReportsFiltersUI from "./ReportsFiltersUI/ReportsFiltersUI";
import ConversionsResultsUI from "../ConversionsResultsUI/ConversionsResultsUI";
import GenericTable from "../../common/table/GenericTable";
import {formatNumber, formatTurkishLira} from "../../common/utils";
import {Avatar} from "@mui/material";

const TABLE_HEADER_BG = "#2C3E50";
const NUMERIC_CELL = {align: 'right', sx: {fontVariantNumeric: 'tabular-nums'}};
const NUMERIC_HEAD = {align: 'right'};

const TABLE_TITLES = {
    0: "Ürün Bazlı Satışlar",
    1: "Influencer Bazlı Satışlar",
    2: "Marka Bazlı Satışlar"
};

// Filtre/arama sonrası kalan satırların toplamı - sayfalama toplamı bozmasın diye
// pre-pagination satır modeli kullanılıyor.
const sumColumn = (table, key) => table.getPrePaginationRowModel().rows
    .reduce((acc, row) => acc + (Number(row.original[key]) || 0), 0);

const ProductPerformance = () => {
    const {isToastOpen, toggleToast, toastContent} = useToast();
    const [conversionResult, setConversionResult] = useState(null);
    const [payload, setPayload] = useState(null);

    const imageColumn = {
        accessorKey: 'image',
        header: "Görsel",
        size: 90,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: (cell) => (
            <Avatar variant="rounded"
                    sx={{width: 56, height: 56, bgcolor: '#F1F4F7'}}
                    alt={cell.row.original.title}
                    src={cell.row.original.image}/>
        )
    };

    const titleColumn = {
        accessorKey: 'title',
        header: "Ürün",
        size: 320,
        Cell: (cell) => (
            <div className={styles.productCell}>
                <a href={cell.row.original.link ? "https://www.karaca.com" + cell.row.original.link : "https://www.karaca.com"}
                   target={"_blank"} rel="noreferrer">{cell.row.original.title}</a>
                {!!cell.row.original.product_id &&
                    <span className={styles.productCode}>Ürün kodu: {cell.row.original.product_id}</span>}
            </div>
        )
    };

    const resultColumns = {}
    resultColumns.visibility = {}
    resultColumns.data = [
        imageColumn,
        titleColumn,
        {
            accessorKey: 'quantity',
            header: "Adet",
            size: 90,
            muiTableHeadCellProps: NUMERIC_HEAD,
            muiTableBodyCellProps: NUMERIC_CELL
        },
        {
            accessorKey: 'brand',
            header: "Marka"
        },
        {
            accessorKey: 'category_name',
            header: "Kategori",
        },
        {
            accessorKey: 'price',
            header: "Birim Fiyat",
            muiTableHeadCellProps: NUMERIC_HEAD,
            muiTableBodyCellProps: NUMERIC_CELL,
            Cell: (cell) => (
                <span>{formatTurkishLira.format(cell.row.original.price)}</span>
            )
        },
        {
            // Karaca satırlarında sunucu gerçek sale_amount gönderir; göndermeyen
            // eski/diğer akışlarda fiyat x adet tahminine düşülür.
            accessorKey: 'sale_amount',
            header: "Toplam Satış",
            muiTableHeadCellProps: NUMERIC_HEAD,
            muiTableBodyCellProps: NUMERIC_CELL,
            Cell: (cell) => {
                const { sale_amount, price, quantity } = cell.row.original;
                const value = (sale_amount !== undefined && sale_amount !== null)
                    ? sale_amount : price * quantity;
                return <span>{formatTurkishLira.format(value)}</span>;
            }
        }
    ];

    const productsColumns = [
        imageColumn,
        titleColumn,
        {
            accessorKey: 'quantity',
            header: "Adet",
            size: 90,
            muiTableHeadCellProps: NUMERIC_HEAD,
            muiTableBodyCellProps: NUMERIC_CELL,
            // Kesilen sepet verisinde görünmeyen kalemler sayılamıyor; toplam alt sınırdır.
            Footer: ({table}) => (
                <span className={styles.footerValue}>
                    {formatNumber(sumColumn(table, 'quantity'))}
                    <span className={styles.footerNote}>görünen kalemler</span>
                </span>
            )
        },
        {
            accessorKey: 'brand',
            header: "Marka"
        },
        {
            accessorKey: 'category_name',
            header: "Kategori",
        },
        {
            accessorKey: 'price',
            header: "Birim Fiyat",
            muiTableHeadCellProps: NUMERIC_HEAD,
            muiTableBodyCellProps: NUMERIC_CELL,
            Cell: (cell) => (
                <span>{formatTurkishLira.format(cell.row.original.price)}</span>
            )
        },
        {
            // Ciro / adet: dönem içinde gerçekten ödenen ortalama birim fiyat.
            // Birim Fiyat (katalog liste fiyatı) ile fark, indirim derinliğini gösterir.
            id: 'avg_paid_price',
            header: "Ort. Satış Fiyatı",
            accessorFn: (row) => (Number(row.quantity) > 0 && Number(row.sale_amount) > 0)
                ? row.sale_amount / row.quantity : null,
            muiTableHeadCellProps: NUMERIC_HEAD,
            muiTableBodyCellProps: NUMERIC_CELL,
            Cell: (cell) => {
                const q = Number(cell.row.original.quantity);
                const rev = Number(cell.row.original.sale_amount);
                return (q > 0 && rev > 0)
                    ? <span>{formatTurkishLira.format(rev / q)}</span>
                    : <span>—</span>;
            }
        },
        {
            accessorKey: 'sale_amount',
            header: "Toplam Satış",
            muiTableHeadCellProps: NUMERIC_HEAD,
            muiTableBodyCellProps: NUMERIC_CELL,
            Cell: (cell) => (
                <span className={styles.revenueValue}>
                    {formatTurkishLira.format(cell.row.original.sale_amount)}
                </span>
            ),
            Footer: ({table}) => (
                <span className={styles.footerValue}>
                    {formatTurkishLira.format(sumColumn(table, 'sale_amount'))}
                </span>
            )
        }
    ];

    const influencersColumns = [
        {
            accessorKey: 'affiliate_company',
            header: "Influencer",
        },
        {
            accessorKey: 'total_quantity',
            header: "Toplam Adet",
            muiTableHeadCellProps: NUMERIC_HEAD,
            muiTableBodyCellProps: NUMERIC_CELL,
            Cell: (cell) => (
                <span>{formatNumber(cell.row.original.products.reduce((acc, item) => acc + item.quantity, 0))}</span>
            )
        },
        {
            accessorKey: 'total_sales',
            header: "Toplam Satış",
            muiTableHeadCellProps: NUMERIC_HEAD,
            muiTableBodyCellProps: NUMERIC_CELL,
            Cell: (cell) => (
                <span className={styles.revenueValue}>{formatTurkishLira.format(cell.row.original.total_sales)}</span>
            ),
            Footer: ({table}) => (
                <span className={styles.footerValue}>
                    {formatTurkishLira.format(sumColumn(table, 'total_sales'))}
                </span>
            )
        }
    ];

    const brandsColumns = [
        {
            accessorKey: 'brand_name',
            header: "Marka",
        },
        {
            accessorKey: 'total_quantity',
            header: "Toplam Adet",
            muiTableHeadCellProps: NUMERIC_HEAD,
            muiTableBodyCellProps: NUMERIC_CELL,
            Cell: (cell) => (
                <span>{formatNumber(cell.row.original.products.reduce((acc, item) => acc + item.quantity, 0))}</span>
            )
        },
        {
            accessorKey: 'total_sales',
            header: "Toplam Satış",
            muiTableHeadCellProps: NUMERIC_HEAD,
            muiTableBodyCellProps: NUMERIC_CELL,
            Cell: (cell) => (
                <span className={styles.revenueValue}>{formatTurkishLira.format(cell.row.original.total_sales)}</span>
            ),
            Footer: ({table}) => (
                <span className={styles.footerValue}>
                    {formatTurkishLira.format(sumColumn(table, 'total_sales'))}
                </span>
            )
        }
    ]


    const productsResultsTableColumns = {}
    productsResultsTableColumns.visibility = {}
    productsResultsTableColumns.data = productsColumns;

    const influencerResultsTableColumns = {}
    influencerResultsTableColumns.visibility = {}
    influencerResultsTableColumns.data = influencersColumns;

    const brandResultsTableColumns = {}
    brandResultsTableColumns.visibility = {}
    brandResultsTableColumns.data = brandsColumns;


    const handleGetReport = async (payload) => {
        setConversionResult([]);
        if (!payload.startDate || !payload.endDate) return;
        setPayload(payload);
        const response = await getConversionReport(payload);
        if (!!response.data) {
            setConversionResult(response.data);
        }
    };


    const handleDownloadFile = async (fileType) => {
        const startEndDateValue = {
            startDate: payload.startDate,
            endDate: payload.endDate
        }
        if (!startEndDateValue || !startEndDateValue.startDate || !startEndDateValue.endDate) return;
        const startDate = startEndDateValue.startDate;
        const endDate = startEndDateValue.endDate;
        switch (fileType) {
            case EXPORT_FILE_TYPES.excel:
                window.open(`${(process.env.REACT_APP_DASHBOARD_API ?? "http://localhost:4000") + DOWNLOAD_OFFER_PRODUCTS_EXCEL_DATA}?offerId=${payload.offerId}&startDate=${startDate}&endDate=${endDate}&reportTypeId=${payload.reportTypeId}&type=excel`, "_blank");
                break;
            case EXPORT_FILE_TYPES.csv:
                window.open(`${(process.env.REACT_APP_DASHBOARD_API ?? "http://localhost:4000") + DOWNLOAD_OFFER_PRODUCTS_EXCEL_DATA}?offerId=${payload.offerId}&startDate=${startDate}&endDate=${endDate}&reportTypeId=${payload.reportTypeId}&type=csv`, "_blank");
                break;
            default:
                break
        }
    }

    const expandProducts = (cell) => {
        return (
            <div>
                {cell.row.original.products && cell.row.original.products.length > 0 && <div>
                    <GenericTable tableData={cell.row.original.products}
                                  enableGlobalSearchBar={false}
                                  enableColumnFilters={false}
                                  enableFullScreenToggle={false}
                                  hideAllToolbar={true}
                                  headerBgColor={"#E74C3C"}
                                  outputColumns={resultColumns}></GenericTable>
                </div>}
            </div>
        )
    }

    const hasResults = !!conversionResult && !!conversionResult.results && conversionResult.results.length > 0;

    return (
        <>
            <div className={styles.container}>
                <BreadcrumbsBar/>
                <Card title={t("menu-item.purchase_reports")}>
                    <ReportsFiltersUI
                        onGenerateButtonClicked={handleGetReport}
                        hideOfferSelectionDropdown={false}
                        hideReportTypeDropdown={false}/>

                    {
                        !!conversionResult && <div>

                            {hasResults && <ConversionsResultsUI reports={conversionResult}/>}

                            {hasResults &&
                                <div className={styles.tableBar}>
                                    <div className={styles.tableBarTitle}>
                                        {(!!payload && TABLE_TITLES[payload.reportTypeId]) || "Sonuçlar"}
                                        <span className={styles.tableBarCount}>
                                            {formatNumber(conversionResult.results.length)} kayıt
                                        </span>
                                    </div>
                                    <ExportDropdown handleCb={handleDownloadFile}/>
                                </div>}

                            {!!payload && payload.reportTypeId === 0 &&
                                <GenericTable tableData={conversionResult.results}
                                              headerBgColor={TABLE_HEADER_BG}
                                              initialState={{sorting: [{id: 'sale_amount', desc: true}]}}
                                              outputColumns={productsResultsTableColumns}></GenericTable>
                            }
                            {!!payload && payload.reportTypeId === 1 &&
                                <GenericTable tableData={conversionResult.results}
                                              expand={expandProducts}
                                              headerBgColor={TABLE_HEADER_BG}
                                              initialState={{sorting: [{id: 'total_sales', desc: true}]}}
                                              tableExpandPadding={0}
                                              outputColumns={influencerResultsTableColumns}></GenericTable>
                            }
                            {!!payload && payload.reportTypeId === 2 &&
                                <GenericTable tableData={conversionResult.results}
                                              outputColumns={brandResultsTableColumns}
                                              headerBgColor={TABLE_HEADER_BG}
                                              initialState={{sorting: [{id: 'total_sales', desc: true}]}}
                                              expand={expandProducts}
                                              tableExpandPadding={0}
                                ></GenericTable>
                            }
                        </div>
                    }
                </Card>
            </div>
            <Toast
                isOpen={isToastOpen}
                onClose={toggleToast}
                content={toastContent}
            />
        </>
    )
}

export default ProductPerformance;
